import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles, Loader2, BookOpen, Users2, Check } from "lucide-react";
import { rotatePrompt, formatAttribution } from "@/lib/text";
import PromptCallout from "@/components/PromptCallout";

export default function WeekView() {
  const { weekNumber } = useParams();
  const nav = useNavigate();
  const [week, setWeek] = useState(null);
  const [edition, setEdition] = useState(() => localStorage.getItem("atc_edition") || "adult");
  const [journal, setJournal] = useState("");
  const [coach, setCoach] = useState("");
  const [coaching, setCoaching] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roleplayDone, setRoleplayDone] = useState(false);
  const [assessmentStatus, setAssessmentStatus] = useState(null);

  useEffect(() => {
    (async () => {
      const [w, p, s] = await Promise.all([
        api.get(`/curriculum/weeks/${weekNumber}`, { params: { edition } }),
        api.get("/progress"),
        api.get("/assessment/status").catch(() => ({ data: null })),
      ]);
      setWeek(w.data);
      setCompleted(!!p.data.find((x) => x.week_number === +weekNumber && x.completed));
      setRoleplayDone(localStorage.getItem(`atc_rp_${edition}_${weekNumber}`) === "1");
      setAssessmentStatus(s.data);
      setJournal("");
      setCoach("");
      window.scrollTo(0, 0);
    })();
  }, [weekNumber, edition]);

  const toggleRoleplayDone = () => {
    const next = !roleplayDone;
    setRoleplayDone(next);
    const key = `atc_rp_${edition}_${weekNumber}`;
    if (next) localStorage.setItem(key, "1");
    else localStorage.removeItem(key);
  };

  const [rpFocus, setRpFocus] = useState("all"); // all | A | B

  const focusStyle = (text) => {
    // If a line explicitly starts with "Partner X:" (or "Partner X opens with:" etc.),
    // dim it when the current focus is on the OTHER partner.
    if (rpFocus === "all") return "";
    const m = /partner\s+([ab])\b/i.exec(text);
    if (!m) return "";
    const speaker = m[1].toUpperCase();
    return speaker === rpFocus ? "" : "opacity-30 saturate-0";
  };

  const changeEdition = (e) => {
    setEdition(e);
    localStorage.setItem("atc_edition", e);
  };

  if (!week) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  const saveJournal = async () => {
    if (!journal.trim()) return toast.error("Write something first.");
    setSaving(true);
    try {
      const activePrompt = rotatePrompt(week.reflection_prompts) || `Week ${weekNumber} reflection`;
      await api.post("/journal", { week_number: +weekNumber, entry: journal, prompt: activePrompt });
      toast.success("Journal saved.");
      setJournal("");
    } catch (e) {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const askCoach = async () => {
    if (!journal.trim()) return toast.error("Write a reflection first.");
    setCoaching(true);
    setCoach("");
    try {
      const { data } = await api.post("/coach", { text: journal, kind: "reflection" });
      setCoach(data.reply);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Coach unavailable.");
    } finally {
      setCoaching(false);
    }
  };

  const markComplete = async () => {
    await api.post("/progress", { week_number: +weekNumber, completed: !completed });
    setCompleted(!completed);
    toast.success(!completed ? "Marked complete." : "Marked incomplete.");
  };

  const prev = +weekNumber > 1 ? +weekNumber - 1 : null;
  const next = +weekNumber < 11 ? +weekNumber + 1 : null;

  // Hard gate: Week 1 content is hidden until the Starting Assessment is submitted
  const needStartingAssessment = +weekNumber === 1 && assessmentStatus && !assessmentStatus.start_submitted;
  if (needStartingAssessment) {
    return (
      <article className="max-w-3xl mx-auto" data-testid="week1-assessment-gate">
        <Link to="/app" className="text-sm text-muted-foreground hover:text-foreground mb-8 inline-block" data-testid="back-to-weeks">
          <ArrowLeft className="inline w-4 h-4 mr-1" /> All weeks
        </Link>
        <p className="text-xs uppercase tracking-[0.25em] text-primary mt-4">Week 1 · Before we begin</p>
        <h1 className="font-serif text-5xl mt-3 leading-tight text-balance">Start with a mirror.</h1>
        <div className="mt-10 border-l-2 border-primary pl-6 font-serif italic text-lg leading-relaxed">
          Before Week 1 opens, take a short Spiritual Health Assessment.
          Not a test — a snapshot of where you are right now. In eleven weeks you'll
          answer the same questions again, and we'll show you side-by-side what God has been doing.
        </div>
        <ul className="mt-10 space-y-3 text-muted-foreground max-w-xl">
          <li>• 20 statements across four movements · about 5 minutes</li>
          <li>• 3 short reflection questions · a few sentences each</li>
          <li>• You can save a draft and come back to finish it</li>
          <li>• Private by default — you choose later whether to share with your leader</li>
        </ul>
        <div className="mt-12 flex gap-3 flex-wrap">
          <Button asChild className="rounded-full" data-testid="week1-begin-assessment">
            <Link to="/app/assessment/start">
              {assessmentStatus.start_has_draft ? "Continue your assessment" : "Begin starting assessment"}
            </Link>
          </Button>
        </div>
      </article>
    );
  }

  return (
    <article className="max-w-3xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <Link to="/app" className="text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-weeks">
          <ArrowLeft className="inline w-4 h-4 mr-1" /> All weeks
        </Link>
        <div className="flex items-center gap-4">
          <button onClick={() => nav(`/app/weeks/${weekNumber}/read`)} data-testid="week-reading-mode"
            className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> Reading mode
          </button>
          <div className="flex items-center border border-border text-xs" role="group">
            <button data-testid="edition-adult" onClick={() => changeEdition("adult")}
              className={`px-3 py-1 uppercase tracking-widest ${edition === "adult" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              Adult
            </button>
            <button data-testid="edition-youth" onClick={() => changeEdition("youth")}
              className={`px-3 py-1 uppercase tracking-widest border-l border-border ${edition === "youth" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              Youth
            </button>
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary">Movement · {week.movement}</div>
        </div>
      </div>

      <header className="mb-12">
        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Week {week.week_number}</p>
        <h1 className="font-serif text-5xl mt-3 leading-tight text-balance">{week.title}</h1>
      </header>

      {+weekNumber === 11 && assessmentStatus?.finish_unlocked && !assessmentStatus?.finish_submitted && (
        <section data-testid="week11-finish-assessment-cta"
          className="mb-12 border border-primary/50 bg-primary/5 p-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-xl">
            <div className="text-xs uppercase tracking-[0.2em] text-primary mb-1">Finishing Assessment unlocked</div>
            <p className="font-serif text-lg italic leading-relaxed">
              Eleven weeks on, answer the same questions you did at the start. We'll show you side-by-side what has changed.
            </p>
          </div>
          <Button asChild className="rounded-full" data-testid="week11-begin-finish">
            <Link to="/app/assessment/finish">
              {assessmentStatus.finish_has_draft ? "Continue finishing assessment" : "Begin finishing assessment"}
            </Link>
          </Button>
        </section>
      )}

      {+weekNumber === 11 && assessmentStatus?.finish_submitted && (
        <section data-testid="week11-view-compare-cta"
          className="mb-12 border border-border bg-card p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Comparison</div>
            <p className="font-serif text-lg">Your Week 1 and Week 11 answers, side by side.</p>
          </div>
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/app/assessment/compare">Open the comparison</Link>
          </Button>
        </section>
      )}

      <section className="border-l-2 border-primary pl-6 my-12">
        <div className="text-xs uppercase tracking-widest text-primary mb-2">{week.opening_scripture_ref}</div>
        <p className="font-serif text-xl italic leading-relaxed whitespace-pre-wrap">{week.opening_scripture_text}</p>
      </section>

      <section className="prose-lg max-w-none">
        {week.intro.split("\n\n").map((p, i) => (
          <p key={i} className="mb-6 leading-[1.85] reveal" style={{ animationDelay: `${i * 40}ms` }}>{p}</p>
        ))}
      </section>

      <section className="mt-16">
        <h2 className="font-serif text-2xl mb-6 flex items-center gap-4">
          <span>Key truths</span>
          <span className="flex-1 rule-line mb-1"></span>
        </h2>
        <ul className="space-y-4">
          {week.key_truths.map((t, i) => (
            <li key={i} className="flex gap-4 reveal" style={{ animationDelay: `${i * 60}ms` }}>
              <span className="font-serif text-primary tabular-nums">{String(i + 1).padStart(2, "0")}</span>
              <span className="leading-relaxed">{t}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-16">
        <h2 className="font-serif text-2xl mb-6 flex items-center gap-4">
          <span>Discussion questions</span>
          <span className="flex-1 rule-line mb-1"></span>
        </h2>
        <ol className="space-y-6">
          {week.discussion_questions.map((q, i) => (
            <li key={i} className="flex gap-4 reveal" style={{ animationDelay: `${i * 60}ms` }}>
              <span className="font-serif text-2xl text-primary tabular-nums leading-none pt-1">{i + 1}</span>
              <span className="leading-relaxed">{q}</span>
            </li>
          ))}
        </ol>
      </section>

      {week.roleplay && (
        <section className="mt-16" data-testid="roleplay-section">
          <h2 className="font-serif text-2xl mb-2 flex items-center gap-4">
            <span>Roleplay it out</span>
            <span className="flex-1 rule-line mb-1"></span>
          </h2>
          <p className="text-sm text-muted-foreground italic mb-6 flex items-center gap-2">
            <Users2 className="w-3.5 h-3.5" /> Live exercise — do this out loud, together.
          </p>

          <div className="border border-border bg-card p-8 shadow-[0_8px_30px_rgba(42,42,38,0.06)]">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="text-xs uppercase tracking-[0.2em] text-primary">{week.roleplay.title}</div>
              <div className="flex items-center border border-border text-[10px]" role="group" aria-label="Partner focus">
                {[
                  { k: "all", l: "Both" }, { k: "A", l: "Partner A" }, { k: "B", l: "Partner B" },
                ].map((o, i) => (
                  <button key={o.k} onClick={() => setRpFocus(o.k)} data-testid={`rp-focus-${o.k}`}
                    className={`px-2.5 py-1 uppercase tracking-widest ${i > 0 ? "border-l border-border" : ""} ${rpFocus === o.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">The setup</div>
              <p className={`leading-relaxed transition-opacity ${focusStyle(week.roleplay.setup)}`}>{week.roleplay.setup}</p>
            </div>

            <div className="mt-6 bg-primary/[0.05] border-l-2 border-primary p-5">
              <div className="text-xs uppercase tracking-widest text-primary mb-2">The scenario</div>
              <p className={`font-serif italic text-lg leading-relaxed whitespace-pre-wrap transition-opacity ${focusStyle(week.roleplay.scenario)}`}>{week.roleplay.scenario}</p>
            </div>

            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Debrief — talk through together</div>
              <ol className="space-y-3">
                {week.roleplay.debrief.map((q, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="font-serif text-lg text-primary tabular-nums leading-none pt-0.5">{i + 1}</span>
                    <span className="leading-relaxed">{q}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-8 pt-5 border-t border-border">
              <button onClick={toggleRoleplayDone} data-testid="roleplay-done"
                className={`flex items-center gap-2 text-xs uppercase tracking-widest transition-colors ${roleplayDone ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                <span className={`w-4 h-4 border flex items-center justify-center ${roleplayDone ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                  {roleplayDone && <Check className="w-3 h-3" />}
                </span>
                {roleplayDone ? "Done together" : "Mark as done together"}
              </button>
              {roleplayDone && week.roleplay.recap && (
                <p data-testid="roleplay-recap"
                  className="mt-4 font-serif italic text-primary/90 leading-relaxed reveal">
                  {week.roleplay.recap}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="mt-16 bg-secondary/50 border border-border p-8">
        <div className="text-xs uppercase tracking-widest text-primary mb-3">This week's practice</div>
        <p className="leading-relaxed whitespace-pre-line">{week.weekly_practice}</p>
      </section>

      <section className="mt-16">
        <div className="border border-border p-8 bg-card shadow-[0_8px_30px_rgba(42,42,38,0.06)]">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Memory verse</div>
          <div className="text-xs text-primary mb-4">{week.memory_verse_ref}</div>
          <blockquote className="font-serif text-2xl italic leading-relaxed">{week.memory_verse_text}</blockquote>
          <div data-testid="verse-attribution"
            className="mt-5 text-right font-serif italic text-sm text-muted-foreground tracking-wide">
            {formatAttribution(week.memory_verse_ref)}
          </div>
        </div>
      </section>

      <section className="mt-16">
        <h2 className="font-serif text-2xl mb-4">Reflect this week</h2>
        <p className="text-muted-foreground text-sm mb-4">
          Write honestly. Your entry is private. When you're ready, ask the reflection coach for a gentle,
          grace-filled response.
        </p>
        {week.reflection_prompts?.length > 0 && (
          <PromptCallout
            weekNumber={week.week_number}
            prompt={rotatePrompt(week.reflection_prompts)}
            eyebrow="Today's prompt"
          />
        )}
        <Textarea data-testid="week-journal-input" rows={8} value={journal} onChange={(e) => setJournal(e.target.value)}
          placeholder={rotatePrompt(week.reflection_prompts) || "In what area is God inviting you to grow this week, and what is one step you will take?"}
          className="bg-transparent border-x-0 border-t-0 border-b rounded-none focus-visible:ring-0 focus-visible:border-primary text-base leading-relaxed" />
        <div className="flex flex-wrap gap-3 mt-4">
          <Button onClick={saveJournal} disabled={saving} data-testid="week-save-journal" className="rounded-full">
            {saving ? "Saving…" : "Save to my journal"}
          </Button>
          <Button variant="outline" onClick={askCoach} disabled={coaching} data-testid="week-ask-coach" className="rounded-full">
            {coaching ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Listening…</> : <><Sparkles className="w-4 h-4 mr-2" /> Ask the reflection coach</>}
          </Button>
        </div>
        {coach && (
          <div className="mt-6 border-l-2 border-accent pl-5 py-2 reveal" data-testid="coach-reply">
            <div className="text-xs uppercase tracking-widest text-accent mb-2">Reflection coach</div>
            <p className="whitespace-pre-wrap leading-relaxed text-[0.95rem]">{coach}</p>
          </div>
        )}
      </section>

      <section className="mt-16 border-l-2 border-primary pl-6">
        <div className="text-xs uppercase tracking-widest text-primary mb-2">Closing prayer</div>
        <p className="font-serif text-xl italic leading-relaxed">{week.closing_prayer}</p>
      </section>

      {week.leader_notes && (
        <section className="mt-16 bg-accent/10 border border-accent/30 p-8">
          <div className="text-xs uppercase tracking-widest text-accent mb-3">Leader notes</div>
          <p className="leading-relaxed">{week.leader_notes}</p>
        </section>
      )}

      <div className="mt-20 border-t border-border pt-8 flex flex-wrap items-center justify-between gap-4">
        <Button variant="ghost" data-testid="week-complete-btn" onClick={markComplete}
          className={completed ? "text-primary" : ""}>
          <CheckCircle2 className="w-4 h-4 mr-2" /> {completed ? "Completed" : "Mark this week complete"}
        </Button>
        <div className="flex gap-2">
          {prev && <Button variant="outline" data-testid="week-prev" onClick={() => nav(`/app/weeks/${prev}`)}><ArrowLeft className="w-4 h-4 mr-1" /> Week {prev}</Button>}
          {next && <Button data-testid="week-next" onClick={() => nav(`/app/weeks/${next}`)} className="rounded-full">Week {next} <ArrowRight className="w-4 h-4 ml-1" /></Button>}
          {!next && <Button data-testid="week-covenant" onClick={() => nav("/app/covenant")} className="rounded-full">Sign the covenant →</Button>}
        </div>
      </div>
    </article>
  );
}
